import { Download, ExternalLink, FileText, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { QuoteFooter } from "@/components/site/QuoteFooter";
import { Reveal } from "@/components/site/motion";
import { DOWNLOADS } from "@/components/site/data";
import { Seo } from "@/components/site/Seo";

export default function DownloadsPage() {
  return (
    <>
      <Seo
        path="/downloads"
        title="Downloads"
        description="Product brochures, specifications, the NCC 2022 Clause 10.8.3 ventilation guide, FAQs and warranty information for the complete SolarArk range."
      />
      <PageHeader
        overline="Resources"
        title="Downloads."
        subtitle="Product brochures, specifications, guides and warranty information for the complete SolarArk range."
      />

      <section data-testid="downloads-section" className="relative py-20 lg:py-28 bg-[#050505] border-t border-[#1c1c1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col gap-16">
          {DOWNLOADS.map((grp, gi) => (
            <div key={grp.group}>
              <Reveal>
                <h2 className="font-display font-semibold text-2xl lg:text-3xl text-white tracking-tight mb-6">{grp.group}</h2>
              </Reveal>
              <div className="grid md:grid-cols-2 gap-4">
                {grp.items.map((it, i) => (
                  <Reveal key={it.title} delay={i * 0.06}>
                    <a
                      href={it.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`download-${gi}-${i}`}
                      className="group flex items-center gap-5 rounded-3xl border border-[#262626] bg-[#0d0d0d] p-6 lg:p-7 hover:border-[#fcc41b]/40 transition-colors duration-400 h-full"
                    >
                      <span className="shrink-0 h-14 w-14 rounded-2xl bg-[#fcc41b]/10 border border-[#fcc41b]/30 flex items-center justify-center text-[#fcc41b] group-hover:bg-[#fcc41b] group-hover:text-black transition-colors duration-400">
                        <FileText size={22} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display font-semibold text-lg text-white leading-snug">{it.title}</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mt-1">{it.desc}</p>
                        <div className="mt-3 flex items-center gap-2 font-mono-eng text-[10px] tracking-wider text-zinc-500">
                          <span className="border border-[#262626] rounded-full px-2.5 py-1">{it.type}</span>
                          {it.size && <span className="border border-[#262626] rounded-full px-2.5 py-1">{it.size}</span>}
                        </div>
                      </div>
                      <span className="shrink-0 text-zinc-500 group-hover:text-[#fcc41b] group-hover:translate-y-0.5 transition-[color,transform] duration-300">
                        {it.external ? <ExternalLink size={20} /> : <Download size={20} />}
                      </span>
                    </a>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
          <Reveal>
            <Link
              to="/warranty-registration"
              data-testid="warranty-register-cta"
              className="group flex flex-col sm:flex-row sm:items-center gap-6 rounded-3xl border border-[#fcc41b]/40 bg-gradient-to-br from-[#161106] to-[#0d0d0d] p-7 lg:p-9 hover:border-[#fcc41b] transition-colors duration-400"
            >
              <span className="shrink-0 h-16 w-16 rounded-2xl bg-[#fcc41b]/15 border border-[#fcc41b]/40 flex items-center justify-center text-[#fcc41b] group-hover:bg-[#fcc41b] group-hover:text-black transition-colors duration-400">
                <ShieldCheck size={28} />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="font-display font-semibold text-xl lg:text-2xl text-white tracking-tight">Register your warranty</h2>
                <p className="text-zinc-400 text-sm lg:text-base leading-relaxed mt-1.5">
                  Just had a SolarArk solar roof ventilation or solar hot water system installed? Register it online to keep your warranty on record.
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#fcc41b] text-black font-display font-semibold px-6 py-3 group-hover:gap-3 transition-[gap] duration-300">
                Register now <ArrowUpRight size={18} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <QuoteFooter blurb="Can't find what you need? Get in touch and our team will send through the right documentation for your project." />
    </>
  );
}
